import * as React from 'react';

import cx from 'classnames';

import { get as userGet } from 'js/lib/user';

import { useMediaQuery } from '@coursera/cds-core';

import { useCoachEpicEnabledState } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import Help from 'bundles/common/components/Help';
import EnterpriseInjectedSearchPageHeader from 'bundles/composable-page-header-enterprise/header-renderers/EnterpriseInjectedSearchPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import { MOBILE_HEADER_BREAKPOINT_MEDIA_QUERY } from 'bundles/composable-page-header/constants';
import EnterpriseSearchBarV2, {
  UnifiedEnterpriseSearchBar,
} from 'bundles/enterprise-learner-search/components/search/EnterpriseSearchBarV2';
import MobileEnterpriseSearchBar, {
  UnifiedMobileEnterpriseSearchBar,
} from 'bundles/enterprise-learner-search/components/search/MobileEnterpriseSearchBar';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import SimpleCourseraMetatags from 'bundles/seo/components/SimpleCourseraMetatags';
import type { TabName as TabNameType } from 'bundles/unified-common/constants/enterprise';

type Props = {
  isAuthenticatedUser: boolean;
  pageTitle?: string;
  programId: string;
  programName: string;
  program: EnterpriseProgramsV1;
  enableSkillsInSearchAndBrowse: boolean;
  thirdPartyOrganizationId: string;
  thirdPartyOrganizationSlug: string;
  className?: string;
  shouldHideSearch?: boolean;
  'data-e2e'?: string;
  shouldShowShortFormContent?: boolean;
  tabNames?: TabNameType[];
};

const ProgramHomeWrapper = ({
  children,
  pageTitle,
  programId,
  program,
  programName,
  thirdPartyOrganizationId,
  thirdPartyOrganizationSlug,
  className,
  shouldHideSearch = false,
  'data-e2e': e2eDataAttribute,
  enableSkillsInSearchAndBrowse,
  shouldShowShortFormContent,
  tabNames,
}: React.PropsWithChildren<Props>) => {
  /*
  We use useShouldShowCoachQuery to determine if we should render the help button. Note, this is not the global help button.
  useChatWidgetSelector doesn't handle this case because it doesn't differentiate between "loading" and "don't show any of the buttons", this is why useShouldShowCoachQuery is used instead.
  */
  const { showEmbeddedCoach } = useCoachEpicEnabledState();
  const isCoachEnabled = showEmbeddedCoach;
  const userId = userGet().id;

  const renderHelpButton = !isCoachEnabled;

  let desktopSearchBar: JSX.Element | null;
  let mobileSearchBar: JSX.Element | null;

  const userAgent = useUserAgent();

  const isMobileWeb = useMediaQuery(MOBILE_HEADER_BREAKPOINT_MEDIA_QUERY, {
    defaultMatches: userAgent?.isMobileBrowser || userAgent?.isMobile,
  });

  if (shouldHideSearch) {
    desktopSearchBar = null;
    mobileSearchBar = null;
  } else if (userId) {
    desktopSearchBar = (
      <UnifiedEnterpriseSearchBar
        programSlug={program?.metadata?.slug}
        programName={program?.metadata?.name}
        thirdPartyOrgSlug={thirdPartyOrganizationSlug}
        thirdPartyOrganizationId={thirdPartyOrganizationId}
        userId={userId}
        programId={programId}
        enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={shouldShowShortFormContent}
      />
    );
    mobileSearchBar = (
      <UnifiedMobileEnterpriseSearchBar
        programSlug={program?.metadata?.slug}
        programName={program?.metadata?.name}
        thirdPartyOrgSlug={thirdPartyOrganizationSlug}
        thirdPartyOrganizationId={thirdPartyOrganizationId}
        userId={userId}
        programId={programId}
        enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={shouldShowShortFormContent}
      />
    );
  } else {
    desktopSearchBar = (
      <EnterpriseSearchBarV2
        programIds={[programId]}
        enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={shouldShowShortFormContent}
        programName={programName}
      />
    );
    mobileSearchBar = (
      <MobileEnterpriseSearchBar
        programIds={[programId]}
        enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={shouldShowShortFormContent}
        programName={programName}
      />
    );
  }

  const wrapperClassName = cx('rc-ProgramHomeWrapper', className);

  return (
    <div className={wrapperClassName} data-e2e={e2eDataAttribute} data-testid="program-home-wrapper">
      <SimpleCourseraMetatags disableCourseraSuffix={Boolean(pageTitle)} title={pageTitle || programName} />
      <UniversalPageFeatures />
      <EnterpriseInjectedSearchPageHeader
        hideSearch={shouldHideSearch}
        injectedSearch={isMobileWeb ? mobileSearchBar : desktopSearchBar}
      />
      {children}
      <PageFooter />
      {renderHelpButton && <Help style={{ minWidth: 0, bottom: 12, right: 12 }} isEnterprise />}
    </div>
  );
};

export default ProgramHomeWrapper;
